import { Star } from "@phosphor-icons/react";
import clsx from "clsx";

import LinkedInAccountsTag from "common/components/LinkedInAccountsTag";
import RenderIf from "common/components/RenderIf";
import ProfileImage from "common/components/ui/ProfileImage";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { getGroupParticipantsString } from "../../utils";
import ConversationDropdown from "../ConversationActions";
import GroupAvatar from "../GroupAvatar/GroupAvatar";
import MessageDetails from "./MessageDetails";
import TimestampDisplay from "./TimestampDisplay";

import type { Conversation } from "../../types";

interface ConversationItemProps {
  conversation: Conversation;
  onSelect: () => void;
  isLoading: boolean;
  isSelected: boolean;
  isPreviousSelected: boolean;
}

export default function ConversationItem({
  conversation,
  onSelect,
  isLoading,
  isSelected,
  isPreviousSelected, // Use this to determine if the previous item is selected
}: ConversationItemProps) {
  const {
    last_message: lastMessage,
    unread_count: unreadCount,
    owner: accountId,
    participants,
  } = conversation;
  const isDesktop = useTwBreakpoint("lg");

  const isMine = accountId === lastMessage.sender.id;
  const isGroup = participants.length > 1;

  const unreadCountComponent = (
    <span className="flex min-h-5 min-w-5 flex-col items-center justify-center rounded-full bg-purple-600 text-xxs-bold text-white">
      {unreadCount > 9 ? "9+" : unreadCount}
    </span>
  );

  return (
    <>
      {/** biome-ignore lint/a11y/useSemanticElements: this "button" has to be a div since it contains a button element */}
      <div
        role="button"
        tabIndex={0}
        className={clsx(
          "group flex flex-col gap-2 border-r-2 p-3 transition-colors duration-100 ease-linear first:border-t first:border-t-black-200 last:mb-0 lg:px-4",
          isSelected
            ? "border-purple-500 bg-purple-50"
            : "border-transparent hover:bg-black-50",
          isLoading && "cursor-not-allowed opacity-50",
        )}
        aria-label={`Conversation with ${participants[0].full_name}`}
        onClick={() => {
          if (isLoading) return;
          onSelect();
        }}
        onKeyDown={(e) => {
          if (isLoading) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        {/* Participant details (name, avatar) */}
        <div className="flex flex-row items-center justify-between gap-1.5">
          <div className="flex flex-row items-center gap-1.5">
            {isGroup ? (
              <GroupAvatar participants={participants} size="sm" />
            ) : (
              <ProfileImage
                className="cursor-pointer rounded-full"
                size="sm"
                src={participants[0].picture_url}
              />
            )}
            <span className="line-clamp-1 text-xs-bold">
              {isGroup
                ? getGroupParticipantsString(participants)
                : participants[0].full_name}
            </span>
            <RenderIf
              condition={
                !isGroup && participants[0].is_lead && participants[0].favorite
              }
            >
              <Star
                className="min-h-3.5 min-w-3.5 text-yellow-500 transition-colors duration-200"
                weight="fill"
                size={16}
              />
            </RenderIf>
          </div>

          <RenderIf condition={isDesktop && !!unreadCount}>
            <div className="relative mb-auto mr-0.5 mt-0.5 group-hover:hidden">
              {unreadCountComponent}
            </div>
          </RenderIf>

          <div className="relative group-hover:block mb-auto lg:hidden">
            <ConversationDropdown conversation={conversation} />
          </div>
        </div>

        {/* Last message from the conversation */}
        <div className="flex flex-row justify-between gap-2">
          <MessageDetails conversation={conversation} isMine={isMine} />

          <RenderIf condition={!isDesktop && !!unreadCount}>
            {unreadCountComponent}
          </RenderIf>
        </div>

        {/* Timestamp details */}
        <div className="flex w-full flex-row items-center justify-between gap-8">
          <LinkedInAccountsTag accountIds={[accountId]} size="sm" />

          <TimestampDisplay
            createdAt={lastMessage.created_at}
            unreadCount={unreadCount}
          />
        </div>
      </div>

      {/* Separator line */}
      <div
        className={clsx(
          "h-px w-full bg-black-200 last:hidden",
          isSelected || isPreviousSelected ? "opacity-0" : "opacity-100",
        )}
      />
    </>
  );
}
